import {
  setAppointment,
  setAppointments,
  setCleanAppointmentsResult,
  setLoadingAppointments,
  setLoadingPutAppointmentStatus,
  setLoadingReserveAppointment,
  setLoadingScheduler,
  setLoadingSchedulerStates,
  setSchedulers,
  setSchedulerStates
} from './actions';
import { call, put, select } from 'redux-saga/effects';
import mainApiClient from '../config/apiClients/apiClient';
import { ApiListRequest, ApiListResponse, ApiWithErrorResponse } from '@febos-io/common';
import { Appointment, AppointmentMeta, Schedule } from '@febos-io/health-common';

type SearchConfigResponse = ApiListResponse & {
  documentos: Schedule[];
};
type SearchAppointmentsResponse = ApiListResponse & {
  documentos: Appointment[];
};

type extractErrorMessageType = (err: unknown, defaultMessage?: string) => string;
const extractErrorMessage: extractErrorMessageType = (err, defaultMessage) => {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === 'string') return err;
  if (defaultMessage) return defaultMessage;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
};

// Tipos para la reserva de citas
export interface ReserveAppointmentRequest {
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  duration: number; // en minutos
  referenceId: string;
  notes?: string;
  metadata?: Partial<AppointmentMeta>;
}

export interface ReserveAppointmentResponse extends ApiWithErrorResponse {
  success: boolean;
  citaId: string;
  cita: Appointment;
}

export interface ReserveAppointmentParams {
  referenceId: string;
  locationId: string;
  appointmentData: ReserveAppointmentRequest;
  callback?: (response: ReserveAppointmentResponse) => void;
}

export type SearchAppointmentsRequest = ApiListRequest & {
  filtros?: string; // Para los filtros como resourceId:xxx|locationId:xxx
  resourceId?: string;
  locationId?: string;
  fechaInicio?: string; // YYYY-MM-DD
  fechaFin?: string; // YYYY-MM-DD
};

// Tipos para los estados de scheduler
export interface SchedulerStatus {
  PK: string;
  SK: string;
  category: string;
  color: string;
  companyId: string;
  createdAt: string;
  description: string;
  isDeleted: boolean;
  name: string;
  position: number;
  statusId: string;
  updatedAt: string;
}

type SearchSchedulerStatesResponse = ApiListResponse & {
  statuses: SchedulerStatus[];
};

// Tipos para actualizar estado de citas
export interface PutAppointmentStatusRequest {
  appointmentId: string;
  statusId: string;
}

export interface PutAppointmentStatusResponse extends ApiWithErrorResponse {
  cita: Appointment;
}

export function* handleSearchSheduler({ payload: { companyId, locationId, params } }) {
  try {
    yield put(setLoadingScheduler(true));
    const response = yield call(() =>
      mainApiClient.get<SearchConfigResponse>(`/v1/schedules/search`, {
        apiId: 'schedulerSearchConfigs',
        params
      })
    );
    const shedulers = response.data.documentos;
    yield put(
      setSchedulers({
        companyId,
        locationId,
        shedulers
      })
    );
  } catch (error) {
    console.error('🚨 Error en handleSearchSheduler:', {
      error: extractErrorMessage(error),
      companyId,
      locationId,
      params,
      timestamp: new Date().toISOString()
    });
  } finally {
    yield put(setLoadingScheduler(false));
  }
}

export function* handleSearchMyAppointments({ payload: { params } }) {
  try {
    yield put(setLoadingAppointments(true));
    const response = yield call(() =>
      mainApiClient.get<SearchAppointmentsResponse>(`/v1/person/me/appointments`, {
        apiId: 'searchAppointments',
        params
      })
    );
    const appointments = response.data.documentos;
    yield put(
      setAppointments({
        companyId: '0',
        locationId: '0',
        appointments
      })
    );
  } catch (error) {
    console.error('🚨 Error en handleSearchMyAppointments:', {
      error: extractErrorMessage(error),
      params,
      timestamp: new Date().toISOString()
    });
  } finally {
    yield put(setLoadingAppointments(false));
  }
}

export function* handleSearchAppointments({ payload: { companyId, locationId, params } }) {
  try {
    yield put(setLoadingAppointments(true));
    const response = yield call(() =>
      mainApiClient.get<SearchAppointmentsResponse>(`/v1/schedules/appointments/search`, {
        apiId: 'searchAppointments',
        params
      })
    );
    const appointments = response.data.documentos;
    yield put(
      setAppointments({
        companyId,
        locationId,
        appointments
      })
    );
  } catch (error) {
    console.error('🚨 Error en handleSearchAppointments:', {
      error: extractErrorMessage(error),
      companyId,
      locationId,
      params,
      timestamp: new Date().toISOString()
    });
  } finally {
    yield put(setLoadingAppointments(false));
  }
}

export function* handleReserveAppointment({
  payload: { referenceId, locationId, appointmentData }
}: {
  payload: ReserveAppointmentParams;
}) {
  try {
    yield put(setLoadingReserveAppointment(true));

    const response = yield call(() =>
      mainApiClient.post<ReserveAppointmentResponse>(`/v1/schedules/reserve`, appointmentData, {
        apiId: 'reserveAppointment',
        params: {
          referenceId,
          locationId
        }
      })
    );
    const reserveAppointmentResponse: ReserveAppointmentResponse = response.data;
    yield put(
      setAppointment({
        appointment: reserveAppointmentResponse.cita
      })
    );
  } catch (error) {
    console.error('🚨 Error en handleReserveAppointment:', {
      error: extractErrorMessage(error),
      referenceId,
      locationId,
      appointmentData,
      timestamp: new Date().toISOString()
    });
  } finally {
    yield put(setLoadingReserveAppointment(false));
  }
}

export function* handleSearchSchedulerStates({ payload: { companyId, params } }) {
  try {
    yield put(setLoadingSchedulerStates(true));
    const response = yield call(() =>
      mainApiClient.get<SearchSchedulerStatesResponse>(`/v1/schedules/status`, {
        apiId: 'searchSchedulerStates',
        params
      })
    );
    const schedulerStates = response.data.statuses;
    yield put(
      setSchedulerStates({
        companyId,
        schedulerStates
      })
    );
  } catch (error) {
    console.error('🚨 Error en handleSearchSchedulerStates:', {
      error: extractErrorMessage(error),
      companyId,
      params,
      timestamp: new Date().toISOString()
    });
  } finally {
    yield put(setLoadingSchedulerStates(false));
  }
}

export function* handleCleanOldAppointments({ payload: { companyId, locationId, monthsBack } }) {
  try {
    console.log(`Starting cleanup of appointments older than ${monthsBack} months`);

    // Calcular la fecha límite (X meses atrás)
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - monthsBack);
    cutoffDate.setHours(0, 0, 0, 0); // Normalizar a inicio del día

    console.log(`Cutoff date: ${cutoffDate.toISOString().split('T')[0]}`);

    // Obtener el estado actual de appointments del store
    const currentState = yield select((state) => state.healthCommon);
    const appointments = currentState?.lists?.[companyId]?.appointments?.[locationId] || {};

    let cleanedCount = 0;
    let remainingCount = 0;
    const cleanedAppointments = {};

    // Revisar cada fecha en appointments
    Object.keys(appointments).forEach((dateKey) => {
      // Convertir la fecha string a objeto Date para comparación precisa
      const appointmentDate = new Date(dateKey + 'T00:00:00.000Z'); // Asegurar UTC
      if (appointmentDate >= cutoffDate) {
        // Mantener appointments que están dentro del rango permitido
        cleanedAppointments[dateKey] = appointments[dateKey];
        remainingCount += appointments[dateKey].length;
      } else {
        // Contar appointments que serán eliminados
        cleanedCount += appointments[dateKey].length;
        console.log(
          `Removing ${appointments[dateKey].length} appointments from ${dateKey} (${
            appointmentDate.toISOString().split('T')[0]
          })`
        );
      }
    });

    // Actualizar el store con los appointments limpios
    if (cleanedCount > 0) {
      yield put({
        type: 'SET_CLEANED_APPOINTMENTS',
        payload: {
          companyId,
          locationId,
          appointments: cleanedAppointments
        }
      });

      console.log(`Cleanup completed: Removed ${cleanedCount} appointments, kept ${remainingCount} appointments`);
    } else {
      console.log('No old appointments found to clean');
    }

    // Enviar resultado de la limpieza
    yield put(
      setCleanAppointmentsResult({
        companyId,
        locationId,
        cleanedCount,
        remainingCount
      })
    );
  } catch (error) {
    console.error('🚨 Error en handleCleanOldAppointments:', {
      error: extractErrorMessage(error),
      companyId,
      locationId,
      monthsBack,
      timestamp: new Date().toISOString()
    });
    yield put(
      setCleanAppointmentsResult({
        companyId,
        locationId,
        cleanedCount: 0,
        remainingCount: 0
      })
    );
  }
}

export function* handlePutAppointmentStatus({
  payload: { appointmentId, statusId }
}: {
  payload: PutAppointmentStatusRequest;
}) {
  try {
    yield put(setLoadingPutAppointmentStatus(true));

    const response = yield call(() =>
      mainApiClient.put<PutAppointmentStatusResponse>(
        `/v1/schedules/appointments/${appointmentId}`,
        { statusId },
        {
          apiId: 'putAppointmentStatus',
          params: {
            appointmentId,
            statusId
          }
        }
      )
    );
    const updatedAppointment: Appointment = response.data.cita;
    // Actualizar la cita en el store
    yield put(
      setAppointment({
        appointment: updatedAppointment
      })
    );
    console.log(`Appointment ${appointmentId} status updated to ${statusId}`);
  } catch (error) {
    console.error('🚨 Error en handlePutAppointmentStatus:', {
      error: extractErrorMessage(error),
      appointmentId,
      statusId,
      timestamp: new Date().toISOString()
    });
  } finally {
    yield put(setLoadingPutAppointmentStatus(false));
  }
}
